/**
 * Centralized Configuration Constants
 * ====================================
 * Single source of truth for contact info, social links, and external URLs.
 * Update values here instead of scattered across components.
 *
 * Environment variables are set by:
 * - Local dev: .env.development (copy from .env.example)
 * - Amplify: SSM Parameter Store (fetched in amplify.yml preBuild)
 *
 * SSM Parameter paths: /mysafespaces/{stage}/api/{service}
 * Where {stage} = dev (develop branch) or prod (main branch)
 */

// =============================================================================
// HELPER: Environment Variable Getter
// =============================================================================
const getEnvVar = (key, fallback = '') => {
  const value = process.env[key];
  if (!value && process.env.NODE_ENV === 'development') {
    console.warn(`Missing env var: ${key}. Using fallback for local dev.`);
    return fallback;
  }
  return value || fallback;
};

// =============================================================================
// CONTACT INFORMATION
// =============================================================================
export const CONTACT = {
  email: {
    general: 'info@mysafespaces.org',
    careers: 'careers@mysafespaces.org',
    partners: 'partners@mysafespaces.org',
    swiss: 'swiss@mysafespaces.org',
  },
  phone: {
    primary: '+91 80626 89525',
    display: '+91 80626 89525',
    href: 'tel:+918062689525',
  },
  location: {
    primary: 'San Diego, CA, USA',
    operations: 'India',
    full: 'San Diego, CA, USA\nOperations: India',
  },
};

// =============================================================================
// SOCIAL MEDIA LINKS
// =============================================================================
export const SOCIAL_LINKS = {
  linkedin: 'https://linkedin.com/company/mysafespaces',
  twitter: 'https://twitter.com/mysafespaces',
  instagram: 'https://instagram.com/mysafespaces',
  facebook: 'https://facebook.com/mysafespaces',
};

// =============================================================================
// PORTAL & PRODUCT LINKS
// =============================================================================
// SSM paths: /mysafespaces/{stage}/portal/{name}
export const PORTAL_LINKS = {
  studentPortal: getEnvVar('REACT_APP_STUDENT_PORTAL_URL'),
  adminPortal: getEnvVar('REACT_APP_ADMIN_PORTAL_URL'),
  thriveAcademy: getEnvVar('REACT_APP_THRIVE_ACADEMY_URL'),
  swissPortal: getEnvVar('REACT_APP_SWISS_PORTAL_URL'),
  swissWebsite: getEnvVar('REACT_APP_SWISS_WEBSITE_URL'),
};

// =============================================================================
// APP STORE LINKS
// =============================================================================
export const APP_STORE_LINKS = {
  android: 'https://play.google.com/store/apps/details?id=com.mysafespaces',
  ios: null, // Coming soon
};

// =============================================================================
// EXTERNAL SERVICE URLS
// =============================================================================
// SSM paths: /mysafespaces/{stage}/services/{name}
export const EXTERNAL_SERVICES = {
  intellicareWidget: getEnvVar('REACT_APP_INTELLICARE_WIDGET_URL'),
};

// =============================================================================
// API ENDPOINTS
// =============================================================================
// SSM paths: /mysafespaces/{stage}/api/{service}
export const API_ENDPOINTS = {
  // Blog Service - posts, categories, subscribers, newsletter
  blog: getEnvVar('REACT_APP_BLOG_API_URL'),

  // CRM Service - lead management
  crm: getEnvVar('REACT_APP_CRM_API_URL'),

  // Jobs Service - career listings, applications
  jobs: getEnvVar('REACT_APP_JOBS_API_URL'),

  // Partners Service - partner network applications
  partners: getEnvVar('REACT_APP_PARTNER_API_URL'),

  // Webinar Service - registration, upcoming events
  webinar: getEnvVar('REACT_APP_WEBINAR_API_URL'),

};

// =============================================================================
// COMPANY INFO
// =============================================================================
export const COMPANY = {
  name: 'My Safe Spaces',
  legalName: 'My Safe Spaces (NxtGen Technologies LLC)',
  tagline: "India's first integrated mental wellness ecosystem, empowering human potential through accessible, intelligent, and compassionate care.",
  foundedYear: 2023,
};

// =============================================================================
// UI CONSTANTS
// =============================================================================
export const UI_CONSTANTS = {
  // Form submission delays (ms)
  successMessageDelay: 3000,

  // Polling intervals (ms)
  polling: {
    base: 30000,
    min: 15000,
    max: 120000,
  },

  // Phone input placeholder (Indian format)
  phonePlaceholder: '+91 XXXXX XXXXX',
};

// =============================================================================
// TAISI PARTNERSHIP
// =============================================================================
// Configuration for TAISI (The Association of International Schools of India)
// partnership landing page.

export const TAISI = {
  name: 'TAISI',
  fullName: 'The Association of International Schools of India',
  website: 'https://taisi.in',
  logo: '/partners/taisi-logo.svg',
  landingPassword: getEnvVar('REACT_APP_TAISI_LANDING_PASSWORD', ''),
  product: 'swiss-k12',
};

export default {
  CONTACT,
  SOCIAL_LINKS,
  PORTAL_LINKS,
  APP_STORE_LINKS,
  EXTERNAL_SERVICES,
  API_ENDPOINTS,
  COMPANY,
  UI_CONSTANTS,
  TAISI,
};
